import { useQueryGetAllBots } from "@apis:bot/bot";
import { useQueryGetAllOrganizations } from "@apis:organization/organization";
import { SuperDataGrid } from "@components:SuperDataGrid";
import { usePaginationSortDataGrid } from "@hooks:usePaginationSortDataGrid";
import { useTableFilter } from "@hooks:useTableFilter";
import { Box, useTheme } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { BotDeleteModal } from "@pages:settings/Tenent/Bots/BotDeleteModal";
import { BotModal } from "@pages:settings/Tenent/Bots/BotModal";
import { useBotsData } from "@stores:settings/bots";
import { formatDateTime } from "@utils:datetime";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDelete, MdModeEdit } from "react-icons/md";

export const BotsTable = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpenModalToUpdate, setIsOpenModalToUpdate] = useState(false);
  const [isOpenModalToDelete, setIsOpenModalToDelete] = useState(false);
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const { searchKeyword, state } = useBotsData();
  const { paginationProps, sortProps } = usePaginationSortDataGrid({
    pageSize: 30,
    defaultSortModel: {
      field: "name",
      sort: "asc",
    },
  });

  const { data, isLoading } = useQueryGetAllBots();
  const { data: organizationsData } = useQueryGetAllOrganizations();

  const organizations = useMemo(() => {
    if (!organizationsData) return {};

    return organizationsData.reduce((data: Record<string, string>, org) => {
      data[org.id] = org.name;
      return data;
    }, {});
  }, [organizationsData]);

  const cleanedData = useTableFilter({
    filters: [
      {
        field: "enabled",
        operator: "equals",
        value: state === "active",
      },
      {
        OR: [
          {
            field: "name",
            operator: "contains",
            value: searchKeyword,
          },
          {
            field: "id",
            operator: "contains",
            value: searchKeyword,
          },
        ],
      },
    ],
    data,
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "id",
        headerName: t("common.id"),
        flex: 2,
        cellClassName: "font-mono",
      },
      {
        field: "name",
        headerName: t("common.name"),
        flex: 1,
        cellClassName: "font-mono",
      },
      {
        field: "organization_id",
        headerName: t("tenant.bots.organization"),
        flex: 1,
        valueParser: (_value, row) => row.organization_id,
        valueGetter: (_value, row) =>
          organizations[row.organization_id] || row.organization_id,
        cellClassName: "font-mono",
      },
      {
        field: "created_at",
        headerName: t("common.createdAt"),
        flex: 1,
        valueParser: (_value, row) => row.created_at,
        valueGetter: (_value, row) => formatDateTime(row.created_at),
        cellClassName: "font-mono",
      },
      {
        field: "updated_at",
        headerName: t("common.updatedAt"),
        flex: 1,
        valueParser: (_value, row) => row.updated_at,
        valueGetter: (_value, row) => formatDateTime(row.updated_at),
        cellClassName: "font-mono",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        flex: 1,
        align: "right",
        getActions: (params: any) => {
          const editButton = (
            <GridActionsCellItem
              key="edit"
              icon={<MdModeEdit size={20} />}
              label={t("common.edit")}
              className="textPrimary"
              color="primary"
              onClick={() => {
                setIsOpenModalToUpdate(true);
                setSelectedBotId(params.row.id);
              }}
            />
          );

          const deleteButton = (
            <GridActionsCellItem
              key="delete"
              icon={<MdDelete size={20} />}
              label={t("common.delete")}
              style={{ color: theme.palette.error.main }}
              onClick={async () => {
                setIsOpenModalToDelete(true);
                setSelectedBotId(params.row.id);
              }}
            />
          );

          const actions = [editButton];
          if (params?.row?.enabled) {
            actions.push(deleteButton);
          }

          return actions;
        },
      },
    ],
    [organizations, t, theme.palette.error.main],
  );

  return (
    <Box
      sx={{
        height: "100%",
      }}
    >
      <SuperDataGrid
        columns={columns}
        rows={cleanedData}
        loading={isLoading}
        {...paginationProps}
        {...sortProps}
        for="bots"
        noRowsOverlayText={t("tenant.bots.noneFound")}
        sx={{
          "& .MuiDataGrid-row--borderBottom": {
            backgroundColor: "white !important",
          },
        }}
      />
      {isOpenModalToUpdate && (
        <BotModal
          isOpen={isOpenModalToUpdate}
          onClose={() => setIsOpenModalToUpdate(false)}
          botId={selectedBotId!}
        />
      )}
      {isOpenModalToDelete && (
        <BotDeleteModal
          isOpen={isOpenModalToDelete}
          onClose={() => setIsOpenModalToDelete(false)}
          botId={selectedBotId!}
        />
      )}
    </Box>
  );
};
